import { useSyncExternalStore } from "react";
import { shortDateTime } from "../mail/format";
import { useQuery } from "@tanstack/react-query";
import type { Draft, DraftInput, Outbox, ReplyContext, Signature } from "@dfws/contracts";
import { api, ApiFailure, getCsrf } from "../../lib/api";
import { queryClient } from "../../lib/queries";

// ---- open composers: docked windows (desktop) or one full-screen sheet (phone); inline replies live in the thread ----

export interface OpenComposer {
  draftId: number;
  minimized: boolean;
  maximized: boolean;
  /** Inline composers render inside their conversation instead of the dock. */
  inlineThread?: string;
}

let composers: OpenComposer[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const sub = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const useComposers = () => useSyncExternalStore(sub, () => composers);

export function showComposer(draftId: number, inlineThread?: string) {
  const rest = composers.filter((c) => c.draftId !== draftId);
  composers = [...rest, { draftId, minimized: false, maximized: false, inlineThread }].slice(-4);
  emit();
}
export function hideComposer(draftId: number) {
  composers = composers.filter((c) => c.draftId !== draftId);
  emit();
}
export function patchComposer(draftId: number, patch: Partial<OpenComposer>) {
  composers = composers.map((c) => (c.draftId === draftId ? { ...c, ...patch } : c));
  emit();
}

// ---- toasts: "Sending... Undo", "Sent", errors ----

export interface Toast {
  id: number;
  text: string;
  action?: { label: string; run: () => void };
  until?: number;
  tone?: "error";
}
let toasts: Toast[] = [];
let toastSeq = 1;
const toastListeners = new Set<() => void>();
const emitToasts = () => toastListeners.forEach((l) => l());
export const useToasts = () =>
  useSyncExternalStore(
    (l) => {
      toastListeners.add(l);
      return () => toastListeners.delete(l);
    },
    () => toasts,
  );
export function toast(t: Omit<Toast, "id">, ms = 6000): number {
  const id = toastSeq++;
  toasts = [...toasts.filter((x) => x.text !== t.text), { ...t, id }].slice(-3);
  emitToasts();
  setTimeout(() => dismissToast(id), ms);
  return id;
}
export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emitToasts();
}

// ---- queries ----

export const useSignatures = () => useQuery({ queryKey: ["signatures"], queryFn: () => api<{ items: Signature[] }>("/signatures"), staleTime: 5 * 60_000 });
export const useIdentities = () => useQuery({ queryKey: ["identities"], queryFn: () => api<{ items: string[] }>("/identities"), staleTime: 10 * 60_000 });
export const useOutbox = () => useQuery({ queryKey: ["outbox"], queryFn: () => api<{ items: Outbox[] }>("/outbox"), staleTime: 30_000 });
export const useDraft = (id: number) => useQuery({ queryKey: ["draft", id], queryFn: () => api<Draft>(`/drafts/${id}`), staleTime: Infinity });

// ---- actions ----

export async function startCompose(input: DraftInput, inlineThread?: string): Promise<Draft> {
  const d = await api<Draft>("/drafts", { method: "POST", body: input });
  queryClient.setQueryData(["draft", d.id], d);
  showComposer(d.id, inlineThread);
  return d;
}

/** Opens a reply, reply all or forward to one message, with recipients, quote and the reply signature filled in. */
export async function startReply(
  messageId: number,
  mode: "reply" | "replyAll" | "forward",
  mine: string[],
  recipients: (ctx: ReplyContext) => { to: { name: string | null; email: string }[]; cc: { name: string | null; email: string }[] },
  inlineThread?: string,
): Promise<void> {
  const ctx = await api<ReplyContext>(`/messages/${messageId}/reply-context`);
  const sigs = queryClient.getQueryData<{ items: Signature[] }>(["signatures"]) ?? (await queryClient.fetchQuery<{ items: Signature[] }>({ queryKey: ["signatures"], queryFn: () => api<{ items: Signature[] }>("/signatures") }));
  const sig = sigs.items.find((x) => x.defaultReply) ?? null;
  const r = mode === "forward" ? { to: [], cc: [] } : recipients(ctx);
  const forwardHeader =
    mode === "forward"
      ? `<div class="df-quote"><p>---------- Forwarded message ----------<br>From: ${esc(ctx.from.name ? `${ctx.from.name} <${ctx.from.email}>` : ctx.from.email)}<br>Date: ${esc(new Date(ctx.date).toLocaleString())}<br>Subject: ${esc(ctx.subject)}<br>To: ${esc(ctx.to.map((a) => a.email).join(", "))}</p>${ctx.quoteHtml.replace(/^<div class="df-quote"><p>[^<]*<\/p>/, "")}`
      : ctx.quoteHtml;
  const from = mine.find((m) => [...ctx.to, ...ctx.cc].some((a) => a.email.toLowerCase() === m)) ?? mine[0];
  const d = await startCompose({ mode, refMessageId: messageId, to: r.to, cc: r.cc, quoteHtml: forwardHeader, signatureId: sig?.id ?? null, from }, inlineThread);
  if (mode === "forward" && ctx.attachments.length) {
    const withFiles = await api<Draft>(`/drafts/${d.id}/attach-original`, { method: "POST" });
    queryClient.setQueryData(["draft", d.id], withFiles);
  }
}

/** New message with the default signature for new mail. */
export async function newCompose(to: { name: string | null; email: string }[] = []): Promise<void> {
  const sigs = queryClient.getQueryData<{ items: Signature[] }>(["signatures"]) ?? (await queryClient.fetchQuery<{ items: Signature[] }>({ queryKey: ["signatures"], queryFn: () => api<{ items: Signature[] }>("/signatures") }));
  await startCompose({ mode: "new", to, signatureId: sigs.items.find((x) => x.defaultNew)?.id ?? null });
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

export function saveDraft(id: number, patch: DraftInput): Promise<Draft> {
  return api<Draft>(`/drafts/${id}`, { method: "PUT", body: patch }).then((d) => {
    queryClient.setQueryData(["draft", id], d);
    return d;
  });
}

export async function discardDraft(id: number) {
  hideComposer(id);
  await api(`/drafts/${id}`, { method: "DELETE" }).catch(() => {});
  queryClient.removeQueries({ queryKey: ["draft", id] });
}

// A composer in its own window hands the Undo toast to the main window, then closes.
const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("dfws-compose");
type Sent = { outboxId: number; sendAt: string; undoSeconds: number | null; draftId: number; scheduled: boolean };
channel?.addEventListener("message", (e: MessageEvent<{ type: string } & Sent>) => {
  if (e.data?.type === "sent") announceSent(e.data);
});

/** Sends with the undo window (or at a set time). The window closes at once; Undo reopens the same draft. */
export async function sendDraft(id: number, sendAt?: Date, opts: { popout?: boolean } = {}): Promise<void> {
  const r = await api<{ outboxId: number; sendAt: string; undoSeconds: number | null }>(`/drafts/${id}/send`, { method: "POST", body: sendAt ? { sendAt: sendAt.toISOString() } : {} });
  hideComposer(id);
  const sent: Sent = { ...r, draftId: id, scheduled: Boolean(sendAt) };
  if (opts.popout && channel && window.opener) {
    channel.postMessage({ type: "sent", ...sent });
    window.close();
    return;
  }
  announceSent(sent);
}

function announceSent(r: Sent) {
  const id = r.draftId;
  const sendAt = r.scheduled;
  void queryClient.invalidateQueries({ queryKey: ["outbox"] });
  const undo = async () => {
    try {
      await api(`/outbox/${r.outboxId}/cancel`, { method: "POST" });
      void queryClient.invalidateQueries({ queryKey: ["outbox"] });
      showComposer(id);
      toast({ text: sendAt ? "Schedule cancelled. The draft is open again." : "Sending undone." });
    } catch (err) {
      toast({ text: err instanceof ApiFailure ? err.message : "Too late to undo.", tone: "error" });
    }
  };
  if (r.undoSeconds) toast({ text: "Sending", action: { label: "Undo", run: undo }, until: Date.now() + r.undoSeconds * 1000 }, r.undoSeconds * 1000);
  else toast({ text: `Scheduled for ${shortDateTime(new Date(r.sendAt))}`, action: { label: "Undo", run: undo } }, 8000);
}

export async function cancelOutbox(id: number): Promise<number | null> {
  const r = await api<{ draftId: number | null }>(`/outbox/${id}/cancel`, { method: "POST" });
  void queryClient.invalidateQueries({ queryKey: ["outbox"] });
  return r.draftId;
}

/** Socket events about sends. */
export function onOutboxEvent(ev: { type?: string; error?: string }) {
  if (ev.type === "outbox.sent") {
    toast({ text: "Message sent" }, 4000);
    void queryClient.invalidateQueries({ queryKey: ["outbox"] });
    void queryClient.invalidateQueries({ queryKey: ["threads"] });
    void queryClient.invalidateQueries({ queryKey: ["thread"] });
  }
  if (ev.type === "outbox.failed") {
    toast({ text: ev.error ?? "The message could not be sent.", tone: "error" }, 10_000);
    void queryClient.invalidateQueries({ queryKey: ["outbox"] });
  }
}

/** Attachment upload with progress, same transport as chat files. */
export function uploadAttachment(file: File, onProgress: (f: number) => void): Promise<{ id: string; name: string; mime: string; size: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/v1/compose/files");
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
    xhr.setRequestHeader("X-File-Type", file.type || "application/octet-stream");
    xhr.setRequestHeader("X-CSRF-Token", getCsrf());
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as { id: string; name: string; mime: string; size: number });
      else reject(new ApiFailure(xhr.status, (body as { error?: { code: string; message: string; retryable: boolean } } | null)?.error ?? null));
    };
    xhr.onerror = () => reject(new ApiFailure(0, { code: "network", message: "The upload failed. Check your connection.", retryable: true }));
    xhr.send(file);
  });
}
